"use client";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  months,
  monthsEn,
  toNepaliNumber,
  useNepaliCalendar,
} from "../hooks/useNepaliCalendar";
import { type CalendarDay } from "../lib/api";
import { getCurrentBSDate } from "../lib/dateConverter";
import { normalizeHolidayWeekdays, WEEKDAYS_BS, WEEKDAYS_AD } from "../lib/calendarUtils";
import "./NepaliCalendar.css";

// ============================================================================
// TYPES
// ============================================================================

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;
  color?: string;
  isHoliday?: boolean;
}

export interface NepaliCalendarProps {
  year?: number;
  month?: number;
  bsYear?: number;
  bsMonth?: number;
  showToggle?: boolean;
  calendarMode?: "bs" | "ad";
  holidayWeekdays?: string | string[];
  events?: CalendarEvent[];
  showNationalHoliday?: boolean;
  eventVisibility?: "default" | "line";
  type?: "default" | "full";
  value?: string;
  className?: string;
  calendarWrapperClassName?: string;
  headerClassName?: string;
  titleClassName?: string;
  navButtonClassName?: string;
  weekdaysClassName?: string;
  weekdayClassName?: string;
  calendarDaysClassName?: string;
  calendarDayClassName?: string;
  bsDateClassName?: string;
  adDateClassName?: string;
  toggleWrapperClassName?: string;
  toggleButtonClassName?: string;
  styles?: any;
  prevArrow?: string | React.ReactNode;
  nextArrow?: string | React.ReactNode;
  weekdayLabels?: string[];
  isClickable?: boolean;
  onDateSelect?: (date: CalendarDay) => void;
  onMonthChange?: (year: number, month: number) => void;
  onCalendarModeChange?: (mode: "bs" | "ad") => void;
  onEventClick?: (event: CalendarEvent) => void;
  showNationalHolidayToggle?: boolean;
  onNationalHolidayToggleChange?: (show: boolean) => void;
  showAllNationalHolidays?: boolean;
  tillCurrentYear?: boolean;
  showArrows?: boolean;
  useLaneBasedEventPositioning?: boolean;
}

interface EventPosition {
  event: CalendarEvent;
  row: number;
  startCol: number;
  span: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

const NepaliCalendar: React.FC<NepaliCalendarProps> = ({
  year: initialYear,
  month: initialMonth,
  bsYear: initialBSYear,
  bsMonth: initialBSMonth,
  showToggle = false,
  calendarMode: initialCalendarMode = "bs",
  holidayWeekdays = "Saturday",
  events = [],
  showNationalHoliday: initialShowNationalHoliday = true,
  eventVisibility = "default",
  type = "full",
  value,
  className = "",
  calendarWrapperClassName = "",
  headerClassName = "",
  titleClassName = "",
  navButtonClassName = "",
  weekdaysClassName = "",
  weekdayClassName = "",
  calendarDaysClassName = "",
  calendarDayClassName = "",
  bsDateClassName = "",
  adDateClassName = "",
  toggleWrapperClassName = "",
  toggleButtonClassName = "",
  styles = {},
  prevArrow = "←",
  nextArrow = "→",
  weekdayLabels,
  isClickable = true,
  onDateSelect,
  onMonthChange,
  onCalendarModeChange,
  onEventClick,
  showNationalHolidayToggle = false,
  onNationalHolidayToggleChange,
  showAllNationalHolidays = false,
  tillCurrentYear = false,
  showArrows = true,
  useLaneBasedEventPositioning = true,
}) => {
  // State
  const [showMoreModal, setShowMoreModal] = useState<{
    date: string;
    events: CalendarEvent[];
    position: { x: number; y: number };
  } | null>(null);
  const [hoveredEvent, setHoveredEvent] = useState<{
    event: CalendarEvent;
    position: { x: number; y: number };
  } | null>(null);
  const [hoveredDay, setHoveredDay] = useState<{
    date: string;
    events: CalendarEvent[];
    position: { x: number; y: number };
  } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showNationalHoliday, setShowNationalHoliday] = useState(initialShowNationalHoliday);
  const [adModeDays, setAdModeDays] = useState<CalendarDay[] | null>(null);

  // Hook
  const {
    currentBSYear,
    currentBSMonth,
    isLoading,
    isYearLoading,
    isMonthLoading,
    calendarMode,
    isIndexedDBInitialized,
    showMonthDropdown,
    showYearDropdown,
    selectedBSKey,
    selectedADKey,
    titleRef,
    setShowMonthDropdown,
    setShowYearDropdown,
    getDataForKey,
    getADMonthRange,
    getDisplayMonthYear,
    getDropdownDisplayValues,
    primaryADMonth,
    primaryADYear,
    handleDayClick,
    handlePrevMonth,
    handleNextMonth,
    handleCalendarModeChange,
    handleMonthSelect,
    handleYearSelect,
    fetchAllBSMonthsForADMonth,
  } = useNepaliCalendar({
    year: initialYear,
    month: initialMonth,
    bsYear: initialBSYear,
    bsMonth: initialBSMonth,
    calendarMode: initialCalendarMode,
    value,
    onDateSelect,
    onMonthChange,
    onCalendarModeChange,
  });

  // Memoized values
  const holidayIndexSet = useMemo(() => normalizeHolidayWeekdays(holidayWeekdays), [holidayWeekdays]);

  // Effects
  useEffect(() => {
    setShowNationalHoliday(initialShowNationalHoliday);
  }, [initialShowNationalHoliday]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 500);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (titleRef.current && !titleRef.current.contains(target)) {
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [titleRef, setShowMonthDropdown, setShowYearDropdown]);

  // Fetch AD mode days
  useEffect(() => {
    if (calendarMode === "ad" && isIndexedDBInitialized && fetchAllBSMonthsForADMonth) {
      let adYear: number;
      let adMonth: number;

      if (primaryADMonth !== null && primaryADYear !== null) {
        adYear = primaryADYear;
        adMonth = primaryADMonth;
      } else {
        const displayInfo = getDisplayMonthYear();
        adYear = displayInfo.yearNum;
        adMonth = displayInfo.monthNum;
      }

      if (adYear >= 1921 && adYear <= 2032 && adMonth >= 1 && adMonth <= 12) {
        fetchAllBSMonthsForADMonth(adYear, adMonth).then(setAdModeDays);
      } else {
        setAdModeDays(null);
      }
    } else {
      setAdModeDays(null);
    }
  }, [calendarMode, primaryADMonth, primaryADYear, isIndexedDBInitialized, fetchAllBSMonthsForADMonth]);

  // Helper functions
  const isDateInEvent = (date: string, event: CalendarEvent): boolean => {
    return date >= event.start && date <= event.end;
  };

  const convertDayEventToCalendarEvent = (
    dayEvent: NonNullable<CalendarDay["events"]>[0],
    date: string
  ): CalendarEvent => ({
    id: `day-event-${dayEvent.id}-${date}`,
    title: dayEvent.title || dayEvent.title_en || "",
    description: dayEvent.description || undefined,
    start: date,
    end: date,
    color: dayEvent.national_holiday === 1 ? "#ef4444" : "#3b82f6",
    isHoliday: dayEvent.national_holiday === 1,
  });

  const getAllEventsForDate = (date: string, dayEvents?: CalendarDay["events"]): CalendarEvent[] => {
    const propEvents = events.filter((event) => isDateInEvent(date, event));
    const apiEvents: CalendarEvent[] = [];

    if (showNationalHoliday && dayEvents) {
      const nationalHolidays = dayEvents
        .filter((event) => event.national_holiday === 1)
        .map((event) => convertDayEventToCalendarEvent(event, date));
      apiEvents.push(...nationalHolidays);

      if (showAllNationalHolidays) {
        const otherEvents = dayEvents
          .filter((event) => event.national_holiday === 0)
          .map((event) => convertDayEventToCalendarEvent(event, date));
        apiEvents.push(...otherEvents);
      }
    }

    return [...propEvents, ...apiEvents];
  };

  // Event positioning
  const calculateEventPositions = useCallback((days: CalendarDay[], startingWeekday: number): EventPosition[] => {
    const positions: EventPosition[] = [];
    const MAX_VISIBLE_LANES = 2;
    const allEvents: CalendarEvent[] = [];

    if (type === "full") {
      allEvents.push(...events);
    }

    if (showNationalHoliday && type === "full") {
      days.forEach((day) => {
        if (day.events?.length) {
          day.events
            .filter((e) => e.national_holiday === 1)
            .forEach((e) => allEvents.push(convertDayEventToCalendarEvent(e, day.ad.date)));

          if (showAllNationalHolidays) {
            day.events
              .filter((e) => e.national_holiday === 0)
              .forEach((e) => allEvents.push(convertDayEventToCalendarEvent(e, day.ad.date)));
          }
        }
      });
    }

    if (allEvents.length === 0) return positions;

    const sortedEvents = [...allEvents].sort((a, b) => {
      const aStart = new Date(a.start).getTime();
      const aEnd = new Date(a.end).getTime();
      const bStart = new Date(b.start).getTime();
      const bEnd = new Date(b.end).getTime();
      const aDuration = aEnd - aStart;
      const bDuration = bEnd - bStart;

      if (aDuration > 0 && bDuration === 0) return -1;
      if (aDuration === 0 && bDuration > 0) return 1;
      if (aDuration !== bDuration) return bDuration - aDuration;
      if (aStart !== bStart) return aStart - bStart;
      return aEnd - bEnd;
    });

    const laneOccupancy: Map<string, Set<number>> = new Map();

    const getEventDates = (event: CalendarEvent): string[] => {
      return days.filter((day) => isDateInEvent(day.ad.date, event)).map((day) => day.ad.date);
    };

    const isLaneFree = (lane: number, dates: string[]): boolean => {
      for (const date of dates) {
        if (laneOccupancy.get(date)?.has(lane)) return false;
      }
      return true;
    };

    const assignLane = (lane: number, dates: string[]): void => {
      for (const date of dates) {
        if (!laneOccupancy.has(date)) laneOccupancy.set(date, new Set());
        laneOccupancy.get(date)!.add(lane);
      }
    };

    for (const event of sortedEvents) {
      const eventDates = getEventDates(event);
      if (eventDates.length === 0) continue;

      let assignedLane = -1;
      for (let lane = 0; lane < MAX_VISIBLE_LANES; lane++) {
        if (isLaneFree(lane, eventDates)) {
          assignedLane = lane;
          assignLane(lane, eventDates);
          break;
        }
      }

      if (assignedLane < 0) continue;

      let startIndex = days.findIndex((d) => d.ad.date === eventDates[0]);
      let endIndex = days.findIndex((d) => d.ad.date === eventDates[eventDates.length - 1]);

      if (startIndex === -1) startIndex = 0;
      if (endIndex === -1) endIndex = days.length - 1;
      if (startIndex > endIndex) continue;

      const startCol = (startingWeekday + startIndex) % 7;
      const endCol = (startingWeekday + endIndex) % 7;
      const startRow = Math.floor((startingWeekday + startIndex) / 7);
      const endRow = Math.floor((startingWeekday + endIndex) / 7);

      for (let row = startRow; row <= endRow; row++) {
        const colStart = row === startRow ? startCol : 0;
        const colEnd = row === endRow ? endCol : 6;
        const span = colEnd - colStart + 1;

        positions.push({
          event,
          row: row * 100 + assignedLane,
          startCol: colStart,
          span,
        });
      }
    }

    return positions;
  }, [events, showNationalHoliday, showAllNationalHolidays, type]);

  // Render calendar days
  const renderCalendarDays = () => {
    if (isLoading || isYearLoading || !isIndexedDBInitialized || isMonthLoading) {
      return (
        <div className="nepali-calendar-loading-spinner-container" style={styles.loadingSpinner}>
          <div className="nepali-calendar-loading-spinner" />
          <div className="nepali-calendar-loading-text" style={styles.loadingText}>
            क्यालेन्डर डाटा लोड हुँदैछ...
          </div>
        </div>
      );
    }

    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    if (!currentData) return null;

    let { days, otherData } = currentData;
    let startingWeekday = parseInt(otherData.starting_weekday.number);

    // Correct "today" flag
    const currentBS = getCurrentBSDate();
    if (currentBS && calendarMode === "bs") {
      const todayBSDate = `${currentBS.bs_y}-${String(currentBS.bs_m).padStart(2, "0")}-${String(currentBS.bs_d).padStart(2, "0")}`;
      const isCurrentMonth = currentBS.bs_y === currentBSYear && currentBS.bs_m === currentBSMonth;
      if (isCurrentMonth) {
        days = days.map((day) => ({ ...day, today: day.bs.date === todayBSDate }));
      }
    }

    // AD mode handling
    if (calendarMode === "ad" && adModeDays?.length) {
      days = adModeDays;

      if (currentBS) {
        const todayBSDate = `${currentBS.bs_y}-${String(currentBS.bs_m).padStart(2, "0")}-${String(currentBS.bs_d).padStart(2, "0")}`;
        days = days.map((day) => ({ ...day, today: day.bs.date === todayBSDate }));
      }

      const displayInfo = getDisplayMonthYear();
      const firstDayDate = new Date(displayInfo.yearNum, displayInfo.monthNum - 1, 1);
      startingWeekday = firstDayDate.getDay();
    }

    const daysArray: React.ReactNode[] = [];

    for (let i = 0; i < startingWeekday; i++) {
      daysArray.push(<div key={`empty-${i}`} className="nepali-calendar-day empty" />);
    }

    const eventPositions = calculateEventPositions(days, startingWeekday);

    const eventsPerDay = new Map<number, number>();
    eventPositions.forEach((pos) => {
      for (let col = pos.startCol; col < pos.startCol + pos.span; col++) {
        const weekRow = Math.floor(pos.row / 100);
        const dayIndex = weekRow * 7 + col - startingWeekday;
        if (dayIndex >= 0 && dayIndex < days.length) {
          eventsPerDay.set(dayIndex, (eventsPerDay.get(dayIndex) || 0) + 1);
        }
      }
    });

    days.forEach((day, index) => {
      const weekdayIndex = (startingWeekday + index) % 7;
      const isHolidayWeekday = holidayIndexSet.has(weekdayIndex);
      const isNationalHoliday = (type === "default" || showNationalHoliday) &&
        day.events?.some((event) => event.national_holiday === 1);
      const isHoliday = isHolidayWeekday || (isNationalHoliday && type !== "default");
      const isSelected = calendarMode === "bs" ? selectedBSKey === day.bs.date : selectedADKey === day.ad.date;

      const dayEventsAll = getAllEventsForDate(day.ad.date, day.events);
      const visibleEventsCount = eventsPerDay.get(index) || 0;
      const hasMoreEvents = type === "full" && dayEventsAll.length > visibleEventsCount;

      const nationalHolidays = type === "default" && day.events
        ? day.events.filter((event) => event.national_holiday === 1).map((event) => convertDayEventToCalendarEvent(event, day.ad.date))
        : [];

      daysArray.push(
        <div
          key={index}
          className={`nepali-calendar-day ${calendarDayClassName} ${isHoliday ? "holiday" : ""} ${day.today ? "today" : ""} ${isSelected ? "selected" : ""}`}
          data-national-holiday={type === "default" && isNationalHoliday ? "true" : undefined}
          onClick={(e) => {
            if (!isClickable) return;
            const target = e.target as HTMLElement;
            if (target.closest(".calendar-event-bar") || target.closest(".show-more-button")) return;
            handleDayClick(day);
          }}
          style={{
            ...styles.calendarDay,
            ...(isHoliday ? styles.holiday ?? styles.saturday : {}),
            position: "relative",
            padding: "4px",
            cursor: isClickable ? "pointer" : "default",
          }}
          onMouseEnter={(e) => {
            if (type === "default" && nationalHolidays.length > 0 && !isMobile) {
              const rect = e.currentTarget.getBoundingClientRect();
              setHoveredDay({
                date: day.ad.date,
                events: nationalHolidays,
                position: { x: rect.left + rect.width / 2, y: rect.top },
              });
            }
          }}
          onMouseLeave={() => type === "default" && setHoveredDay(null)}
        >
          {calendarMode === "bs" ? (
            <>
              <div
                className={`bs-date ${bsDateClassName}`}
                style={{
                  position: "absolute",
                  top: "1px",
                  left: day.today ? "20px" : "4px",
                  fontSize: isMobile ? "0.9rem" : "1.1rem",
                  fontWeight: 500,
                  lineHeight: 1,
                  zIndex: 3,
                  ...styles.bsDate,
                  color: day.today || isSelected ? "blue" : isNationalHoliday && type === "default" ? "#ef4444" : styles.bsDate?.color || "#1f2937",
                }}
              >
                {day.bs?.date_np || ""}
              </div>
              <div
                className={`ad-date ${adDateClassName}`}
                style={{
                  position: "absolute",
                  right: "4px",
                  bottom: "4px",
                  fontSize: isMobile ? "9px" : "11px",
                  color: isNationalHoliday && type === "default" ? "#ef4444" : "#111827",
                  opacity: day.today || isSelected ? 0.8 : 0.6,
                  fontWeight: 400,
                  lineHeight: 1,
                  zIndex: 3,
                  ...styles.adDate,
                }}
              >
                {day.ad?.ad_d ?? (day.ad?.date ? day.ad.date.split("-")[2] : "")}
              </div>
            </>
          ) : (
            <div
              className={`ad-date ${adDateClassName}`}
              style={{
                position: "absolute",
                top: "1px",
                left: day.today ? "20px" : "4px",
                fontSize: isMobile ? "0.9rem" : "1.1rem",
                fontWeight: 500,
                lineHeight: 1,
                zIndex: 3,
                ...styles.adDate,
                color: day.today || isSelected ? "blue" : isNationalHoliday && type === "default" ? "#ef4444" : styles.adDate?.color || "#1f2937",
              }}
            >
              {day.ad?.ad_d ?? (day.ad?.date ? day.ad.date.split("-")[2] : "")}
            </div>
          )}

          {hasMoreEvents && (
            <button
              className="show-more-button"
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                setShowMoreModal({ date: day.ad.date, events: dayEventsAll, position: { x: rect.left, y: rect.bottom } });
              }}
              style={{
                position: "absolute",
                bottom: "2px",
                left: "10px",
                transform: "translateX(-50%)",
                fontSize: "8px",
                padding: isMobile ? "1px 3px" : "1px 4px",
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                color: "white",
                border: "none",
                borderRadius: "2px",
                cursor: "pointer",
                zIndex: 3,
                fontWeight: 500,
              }}
            >
              +{dayEventsAll.length - visibleEventsCount}
            </button>
          )}

          {isNationalHoliday && type !== "default" && (
            <div
              style={{
                position: "absolute",
                top: "2px",
                right: "4px",
                width: isMobile ? "6px" : "8px",
                height: isMobile ? "6px" : "8px",
                backgroundColor: "#ef4444",
                borderRadius: "50%",
                zIndex: 3,
              }}
              title="National Holiday"
            />
          )}
        </div>
      );
    });

    const remainingCells = 42 - daysArray.length;
    for (let i = 0; i < remainingCells; i++) {
      daysArray.push(<div key={`empty-end-${i}`} className="nepali-calendar-day empty" />);
    }

    return (
      <>
        {daysArray}
        {eventPositions.map((pos, idx) => {
          const weekRow = Math.floor(pos.row / 100);
          const eventRow = pos.row % 100;

          return (
            <div
              key={`${pos.event.id}-${idx}`}
              className="calendar-event-bar"
              onClick={(e) => { e.stopPropagation(); onEventClick?.(pos.event); }}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredEvent({ event: pos.event, position: { x: rect.left + rect.width / 2, y: rect.top } });
              }}
              onMouseLeave={() => setHoveredEvent(null)}
              style={{
                position: "absolute",
                left: `calc(${(pos.startCol / 7) * 100}% + 4px)`,
                top: `calc(${weekRow * (100 / 6)}% + ${eventRow * (isMobile ? 16 : 20) + (isMobile ? 24 : 28)}px)`,
                width: `calc(${(pos.span / 7) * 100}% - 8px)`,
                height: eventVisibility === "line" ? (isMobile ? "2px" : "3px") : (isMobile ? "14px" : "17px"),
                backgroundColor: pos.event.color || "#3b82f6",
                color: eventVisibility === "line" ? "transparent" : "white",
                fontSize: isMobile ? "9px" : "11px",
                padding: eventVisibility === "line" ? "0" : (isMobile ? "1px 4px" : "2px 6px"),
                borderRadius: eventVisibility === "line" ? "0" : "4px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                fontWeight: 500,
                boxSizing: "border-box",
              }}
            >
              {eventVisibility === "line" ? null : pos.event.title}
            </div>
          );
        })}
      </>
    );
  };

  const renderMonths = () => {
    const monthList = calendarMode === "bs" ? months : monthsEn;
    const { selectedMonth } = getDropdownDisplayValues();
    return monthList.map((month, index) => (
      <div
        key={index}
        className="nepali-calendar-dropdown-item"
        onClick={() => handleMonthSelect(index + 1)}
        style={{ padding: "8px", cursor: "pointer", backgroundColor: selectedMonth === index + 1 ? "#e8f4f8" : "white" }}
      >
        {month}
      </div>
    ));
  };

  const renderYears = () => {
    const years: number[] = [];
    const { selectedYear } = getDropdownDisplayValues();
    const currentBSYearNow = getCurrentBSDate()?.bs_y || 2081;
    const currentADYearNow = new Date().getFullYear();

    if (calendarMode === "bs") {
      const endYear = tillCurrentYear ? currentBSYearNow : 2089;
      for (let i = 2033; i <= endYear; i++) years.push(i);
    } else {
      const endYear = tillCurrentYear ? currentADYearNow : 2033;
      for (let i = 1943; i <= endYear; i++) years.push(i);
    }

    return years.map((y) => (
      <div
        key={y}
        className="nepali-calendar-dropdown-item"
        onClick={() => handleYearSelect(y)}
        style={{ padding: "8px", cursor: "pointer", backgroundColor: selectedYear === y ? "#e8f4f8" : "white" }}
      >
        {calendarMode === "bs" ? toNepaliNumber(y) : y}
      </div>
    ));
  };

  const weekdaysToDisplay = weekdayLabels || (calendarMode === "bs" ? WEEKDAYS_BS : WEEKDAYS_AD);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <style>{`
        .nepali-calendar-day { min-height: ${isMobile ? "90px" : "90px"} !important; height: auto !important; aspect-ratio: auto !important; }
        .nepali-calendar-day.today { ${value ? "background-color: initial !important; border: none !important; border-radius: initial !important;" : "background-color: #dbeafe !important; border-color: #3b82f6 !important; border-radius: 10px !important;"} }
        .nepali-calendar-day.selected { background-color: #87ceeb !important; border-color: #5fb8d5 !important; border-radius: 10px !important; }
        .nepali-calendar-day.selected .bs-date, .nepali-calendar-day.selected .ad-date { color: white !important; }
        .nepali-calendar-day.selected.today { ${value ? "background-color: #87ceeb !important; border-color: #5fb8d5 !important;" : "background-color: #4169e1 !important; border-color: #4169e1 !important;"} }
        ${type === "default" ? ".nepali-calendar-day.holiday:not(.selected):not(.today) .bs-date, .nepali-calendar-day.holiday:not(.selected):not(.today) .ad-date, .nepali-calendar-day[data-national-holiday='true']:not(.selected):not(.today) .bs-date, .nepali-calendar-day[data-national-holiday='true']:not(.selected):not(.today) .ad-date { color: #ef4444 !important; }" : ""}
        .nepali-calendar-days { min-height: ${isMobile ? "420px" : "480px"} !important; grid-auto-rows: minmax(${isMobile ? "70px" : "80px"}, auto) !important; }
      `}</style>

      <div className={`nepali-calendar ${className} ${calendarWrapperClassName}`} style={styles.calendarWrapper}>
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", alignItems: "center", marginBottom: showToggle || showNationalHolidayToggle ? "12px" : "0" }}>
          {showToggle && (
            <div className={`nepali-calendar-toggle-wrapper ${toggleWrapperClassName}`} style={styles.toggleWrapper}>
              <div
                className={`nepali-calendar-toggle-switch ${toggleButtonClassName}`}
                onClick={() => handleCalendarModeChange(calendarMode === "bs" ? "ad" : "bs")}
                style={{ position: "relative", width: "50px", height: "24px", borderRadius: "12px", backgroundColor: calendarMode === "bs" ? "#4169e1" : "#e5e7eb", cursor: "pointer", transition: "background-color 0.3s", ...styles.toggleSwitch }}
              >
                <div style={{ position: "absolute", top: "2px", left: calendarMode === "bs" ? "2px" : "26px", width: "20px", height: "20px", borderRadius: "50%", backgroundColor: "white", boxShadow: "0 2px 4px rgba(0,0,0,0.2)", transition: "left 0.3s", ...styles.toggleSlider }} />
              </div>
              <span style={{ marginLeft: "8px", fontSize: "14px" }}>{calendarMode === "bs" ? "BS" : "AD"}</span>
            </div>
          )}
          {showNationalHolidayToggle && (
            <div className={`nepali-calendar-toggle-wrapper ${toggleWrapperClassName}`} style={styles.toggleWrapper}>
              <div
                className={`nepali-calendar-toggle-switch ${toggleButtonClassName}`}
                onClick={() => { const newValue = !showNationalHoliday; setShowNationalHoliday(newValue); onNationalHolidayToggleChange?.(newValue); }}
                style={{ position: "relative", width: "50px", height: "24px", borderRadius: "12px", backgroundColor: showNationalHoliday ? "#ef4444" : "#e5e7eb", cursor: "pointer", transition: "background-color 0.3s", ...styles.toggleSwitch }}
              >
                <div style={{ position: "absolute", top: "2px", left: showNationalHoliday ? "2px" : "26px", width: "20px", height: "20px", borderRadius: "50%", backgroundColor: "white", boxShadow: "0 2px 4px rgba(0,0,0,0.2)", transition: "left 0.3s", ...styles.toggleSlider }} />
              </div>
              <span style={{ marginLeft: "8px", fontSize: "14px" }}>National Holiday</span>
            </div>
          )}
        </div>

        <div className={`nepali-calendar-header ${headerClassName}`} style={{ ...styles.header, justifyContent: showArrows ? "space-between" : "center" }}>
          {showArrows && (
            <button className={`nepali-calendar-nav-button ${navButtonClassName}`} onClick={handlePrevMonth} disabled={isLoading || isYearLoading || isMonthLoading} style={{ ...styles.navButton, ...(isLoading || isYearLoading || isMonthLoading ? styles.navButtonDisabled : {}) }}>
              {prevArrow}
            </button>
          )}

          <div ref={titleRef} className={`nepali-calendar-title ${titleClassName}`} style={{ ...styles.title, display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", cursor: "pointer", position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div onClick={() => { setShowMonthDropdown(!showMonthDropdown); setShowYearDropdown(false); }} style={{ backgroundColor: showMonthDropdown ? "#e8f4f8" : "transparent", padding: "4px 8px", borderRadius: "4px" }}>
                {getDisplayMonthYear().month}
              </div>
              <span>/</span>
              <div onClick={() => { setShowYearDropdown(!showYearDropdown); setShowMonthDropdown(false); }} style={{ backgroundColor: showYearDropdown ? "#e8f4f8" : "transparent", padding: "4px 8px", borderRadius: "4px" }}>
                {getDisplayMonthYear().year}
              </div>
            </div>

            {calendarMode === "bs" && getADMonthRange() && (
              <div style={{ fontSize: "0.75rem", color: "#6b7280", marginLeft: "4px" }}>{getADMonthRange()}</div>
            )}

            {showMonthDropdown && (
              <div style={{ position: "absolute", top: "100%", left: 0, backgroundColor: "white", border: "1px solid #ccc", borderRadius: "4px", maxHeight: "200px", overflowY: "auto", zIndex: 1000, width: "150px", marginTop: "4px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
                {renderMonths()}
              </div>
            )}
            {showYearDropdown && (
              <div style={{ position: "absolute", top: "100%", right: 0, backgroundColor: "white", border: "1px solid #ccc", borderRadius: "4px", maxHeight: "200px", overflowY: "auto", zIndex: 1000, width: "100px", marginTop: "4px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
                {renderYears()}
              </div>
            )}
          </div>

          {showArrows && (
            <button className={`nepali-calendar-nav-button ${navButtonClassName}`} onClick={handleNextMonth} disabled={isLoading || isYearLoading || isMonthLoading} style={{ ...styles.navButton, ...(isLoading || isYearLoading || isMonthLoading ? styles.navButtonDisabled : {}) }}>
              {nextArrow}
            </button>
          )}
        </div>

        <div className={`nepali-calendar-weekdays ${weekdaysClassName}`} style={styles.weekdays}>
          {weekdaysToDisplay.map((day, index) => (
            <div key={index} className={`nepali-calendar-weekday ${weekdayClassName} ${holidayIndexSet.has(index) ? "holiday" : ""}`} style={{ ...styles.weekday, ...(holidayIndexSet.has(index) ? styles.weekdayHoliday ?? styles.weekdaySaturday : {}) }}>
              {day}
            </div>
          ))}
        </div>

        <div className={`nepali-calendar-days ${calendarDaysClassName}`} style={{ ...styles.calendarDays, position: "relative" }}>
          {renderCalendarDays()}
        </div>
      </div>

      {/* Tooltips */}
      {!isMobile && hoveredEvent && (
        <div style={{ position: "fixed", left: hoveredEvent.position.x, top: hoveredEvent.position.y, transform: "translate(-50%, calc(-100% - 8px))", backgroundColor: "rgba(0, 0, 0, 0.9)", color: "white", padding: "8px 12px", borderRadius: "6px", fontSize: "12px", maxWidth: "250px", zIndex: 10000, pointerEvents: "none", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)" }}>
          <div style={{ fontWeight: 600, marginBottom: "4px" }}>{hoveredEvent.event.title}</div>
          {hoveredEvent.event.description && <div>{hoveredEvent.event.description}</div>}
        </div>
      )}

      {!isMobile && hoveredDay?.events.length && (
        <div style={{ position: "fixed", left: hoveredDay.position.x, top: hoveredDay.position.y, transform: "translate(-50%, calc(-100% - 8px))", backgroundColor: "rgba(0, 0, 0, 0.9)", color: "white", padding: "12px", borderRadius: "6px", fontSize: "12px", maxWidth: "300px", zIndex: 10000, pointerEvents: "none", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)" }}>
          <div style={{ fontWeight: 600, marginBottom: "8px", fontSize: "13px", borderBottom: "1px solid rgba(255, 255, 255, 0.2)", paddingBottom: "6px" }}>National Holidays</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {hoveredDay.events.map((event) => (
              <div key={event.id}>
                <div style={{ fontWeight: 500, marginBottom: "4px" }}>{event.title}</div>
                {event.description && <div style={{ fontSize: "11px", opacity: 0.9, marginTop: "2px" }}>{event.description}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Show more modal */}
      {showMoreModal && (
        <>
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.3)", zIndex: 9998 }} onClick={() => setShowMoreModal(null)} />
          <div style={{ position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)", backgroundColor: "white", borderRadius: "8px", padding: "16px", boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)", zIndex: 9999, minWidth: isMobile ? "90%" : "300px", maxWidth: isMobile ? "90%" : "400px", maxHeight: "80vh", overflowY: "auto" }}>
            <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: 600 }}>Events on {showMoreModal.date}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {showMoreModal.events.map((event) => (
                <div key={event.id} onClick={() => { onEventClick?.(event); setShowMoreModal(null); }} style={{ padding: "12px", backgroundColor: event.color || "#3b82f6", color: "white", borderRadius: "6px", cursor: "pointer", transition: "transform 0.2s" }}>
                  <div style={{ fontWeight: 600, marginBottom: "4px" }}>{event.title}</div>
                  {event.description && <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px" }}>{event.description}</div>}
                  <div style={{ fontSize: "11px", marginTop: "4px", opacity: 0.8 }}>{event.start === event.end ? event.start : `${event.start} - ${event.end}`}</div>
                </div>
              ))}
            </div>
            <button onClick={() => setShowMoreModal(null)} style={{ marginTop: "12px", width: "100%", padding: "10px", backgroundColor: "#f3f4f6", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>Close</button>
          </div>
        </>
      )}
    </div>
  );
};

export default NepaliCalendar;